import React, { useContext } from 'react'
import type { ButtonProps } from '@livechat/design-system-react-components'

import type { IApplication } from '@/apis/apps/interfaces'
import { useOnboarding } from '@/context/OnboardingContext'
import { ViewModeContext } from '@/context/viewMode'
import useParamSelector from '@/hooks/useParamSelector'
import {
  checkIfIsService,
  canInstallApplications,
  getAlternativeSettingUrl,
  isInternalProduct,
  hasAppFullscreenWidget,
} from '@/store/selectors'
import { VIEW_MODES } from '@/constants'
import { useTypedSelector } from '@/store/hooks'
import OnboardingBadge from '@/components/Product/Badges/OnboardingBadge'
import { ONBOARDING_BADGES } from '@/components/Product/Badges'

import ActivatedButton from './Activated'
import SettingsButton from './Settings'
import OnboardingButton from './Onboarding'
import OpenAppButton from './OpenApp'
import OpenFullscreenWidgetButton from './OpenFullscreenWidget'

interface IInstalledButtonProps extends ButtonProps {
  id: IApplication['id']
}

const InstalledButton = (props: IInstalledButtonProps) => {
  const { id } = props
  const canUserInstallApplications = useTypedSelector(canInstallApplications)
  const isService = useParamSelector(checkIfIsService, id)
  const hasAlternativeSettingsUrl = !!useParamSelector(
    getAlternativeSettingUrl,
    id
  )
  const isAppInternalProduct = useParamSelector(isInternalProduct, id)
  const hasFullscreenWidget = useParamSelector(hasAppFullscreenWidget, id)

  const { hasAcceptedConsent, isConfigured } = useOnboarding()
  const viewMode = useContext(ViewModeContext)

  const isEditorial = viewMode === VIEW_MODES.EDITORIAL

  switch (true) {
    case isService:
      return <ActivatedButton {...props} />

    case canUserInstallApplications && (!hasAcceptedConsent || !isConfigured):
      return <OnboardingButton {...props} />

    case isEditorial:
      return <OnboardingBadge {...ONBOARDING_BADGES.installed} />

    case isAppInternalProduct && hasAlternativeSettingsUrl:
      return <OpenAppButton {...props} />

    case hasFullscreenWidget:
      return <OpenFullscreenWidgetButton {...props} />

    default:
      return <SettingsButton {...props} />
  }
}

export default InstalledButton
